import parsePhoneNumberFromString from "libphonenumber-js";

// The sign up form asks for five things — name, email, password, its
// confirmation and a phone number — and /Account/register wants a sixth: an
// organization name. It also has a country and an organization type on it,
// which it does not insist on but which the account is better off carrying than
// leaving at nothing.
//
// So the three are filled in from what the form does ask for, rather than put
// back on it as questions nobody has to answer to open an account:
//
//   organization name   the person's own name, which is what a fresh account is
//   country             the country the phone number belongs to
//   organization type   "Other", until somebody says otherwise
//
// All three are the account's to correct — Account settings edits every one of
// them (components/ProfileInformation) — and none of them decides anything on
// the way through sign up: the level the organization registers under comes
// from the package it picks (accountLevel.js), and the address an invoice is
// made out to comes from the card form (pages/auth/payment/StripeForm).

/** The organization type a sign up is filed under when nobody has picked one. */
const DEFAULT_ORGANIZATION_TYPE = "Other";

/**
 * The organization a person who has only given their name is registering: their
 * own name. Blank is not worth sending — the endpoint refuses it, and the
 * validator on the field has already asked for one.
 */
export const deriveOrganizationName = (fullName) => fullName?.trim() || undefined;

/**
 * The country a phone number was dialled from, as the two-letter code both the
 * country list and Stripe name a country by. The number has already been
 * through libphonenumber by the time this is asked — the field will not
 * validate otherwise — so what it says the country is, is as good as an answer
 * to a question nobody was asked.
 *
 * Undefined when the number names no country. Every caller treats that as a
 * field left blank rather than a failure: the register endpoint does not
 * require a country, and the card form leaves the guess there to be corrected.
 */
export const deriveCountryCode = (phone) =>
   phone ? parsePhoneNumberFromString(phone, "US")?.country : undefined;

/**
 * The same country, as one of the ids /Languages/getAllCountries reports, which
 * is what the register endpoint takes.
 *
 * Undefined when the number names no country or the list has not arrived. The
 * register endpoint does not require the field, so an account opens either way
 * and Account settings is where a country that was guessed wrong is put right.
 */
export const deriveCountryId = (phone, countries) => {
   const country = deriveCountryCode(phone);
   if (!country) return undefined;

   return countries?.find((item) => item.code?.toUpperCase() === country)?.id;
};

/**
 * The organization type a sign up is registered under. "Other" is the one that
 * claims nothing about an account nobody has asked to describe; if the list
 * ever loses it, the first type is still better than none.
 */
export const deriveOrganizationTypeId = (organizationTypes) => {
   const types = organizationTypes ?? [];
   const fallback = types[0];

   return (
      types.find(
         (type) => type.name?.trim().toLowerCase() === DEFAULT_ORGANIZATION_TYPE.toLowerCase()
      )?.id ?? fallback?.id
   );
};
